import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Calendar,
  MapPin,
  Users,
  DollarSign,
  FileText,
  CheckCircle,
  Shield,
  User,
  Building,
  Hammer,
  List,
  Info,
  Wallet,
  PlusCircle,
} from "lucide-react";
import axios from "axios";
import { MyDomain } from "../../../App";

export default function BossCreateDayWork({
  visible,
  onClose,
  onSuccess,
  places = [],
  employees = [],
  preselectedDate = "",
  preselectedPlaceId = null,
  preselectedEmployees = [],
}) {
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlaceType, setSelectedPlaceType] = useState(null);
  const [userData, setUserData] = useState(null);
  const [allData, setAllData] = useState(null);

  // Tracks the last place we auto-loaded employees for — prevents duplicate loads
  const lastLoadedPlaceIdRef = useRef(null);

  // Tracks original employee IDs for update-mode disable logic
  const originalEmployeeIdsRef = useRef([]);

  // Load data from localStorage
  useEffect(() => {
    const user = localStorage.getItem("user");
    const data = localStorage.getItem("allData");
    if (user) setUserData(JSON.parse(user));
    if (data) setAllData(JSON.parse(data));
  }, []);

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${day}-${month}-${year}`;
  };

  // ── Auto-load employees from existing work entries for a place + date ──
  const loadEmployeesForPlaceAndDate = (
    placeId,
    selectedDate,
    currentAllData,
  ) => {
    if (!placeId || !selectedDate || !currentAllData) return;

    const workEntries = (currentAllData.myDailyWork || []).filter(
      (work) => work.placeId === placeId && work.date === selectedDate,
    );

    const place = (currentAllData.myPlaces || []).find((p) => p.id === placeId);

    if (workEntries.length > 0) {
      const employeesWithData = workEntries
        .map((work) => {
          const employee = (currentAllData.myEmployees || []).find(
            (emp) => emp.id === work.employeeId,
          );
          if (!employee) return null;
          return {
            id: employee.id,
            name: employee.name,
            salary:
              work.salary?.toString() || employee.salary?.toString() || "",
            note: work.note || "",
            placeDailySalary:
              work.placeDailySalary?.toString() ||
              place?.placeDailySalary?.toString() ||
              "",
          };
        })
        .filter(Boolean);

      setSelectedEmployees(employeesWithData);
    } else {
      setSelectedEmployees([]);
    }

    lastLoadedPlaceIdRef.current = placeId;
  };

  // ── Initialise modal when it opens ──
  useEffect(() => {
    if (visible && allData) {
      const initialDate = preselectedDate || getTodayDate();
      setDate(initialDate);
      setSelectedPlaceId(preselectedPlaceId);

      if (preselectedEmployees && preselectedEmployees.length > 0) {
        const place = (allData.myPlaces || []).find(
          (p) => p.id === preselectedPlaceId,
        );

        const employeesWithNotes = preselectedEmployees.map((emp) => {
          const existingWork = (allData.myDailyWork || []).find(
            (work) =>
              work.employeeId === emp.id &&
              work.date === initialDate &&
              work.placeId === preselectedPlaceId,
          );
          return {
            id: emp.id,
            name: emp.name,
            salary: emp.salary?.toString() || "",
            note: existingWork?.note || emp.note || "",
            placeDailySalary:
              existingWork?.placeDailySalary?.toString() ||
              place?.placeDailySalary?.toString() ||
              "",
          };
        });

        setSelectedEmployees(employeesWithNotes);
        originalEmployeeIdsRef.current = employeesWithNotes.map((e) => e.id);
        lastLoadedPlaceIdRef.current = preselectedPlaceId;
      } else if (preselectedPlaceId) {
        loadEmployeesForPlaceAndDate(preselectedPlaceId, initialDate, allData);
        originalEmployeeIdsRef.current = [];
      } else {
        setSelectedEmployees([]);
        originalEmployeeIdsRef.current = [];
        lastLoadedPlaceIdRef.current = null;
      }
    }
  }, [
    visible,
    preselectedDate,
    preselectedPlaceId,
    preselectedEmployees,
    allData,
  ]);

  // ── Re-load employees when the selected place changes ──
  useEffect(() => {
    if (visible && selectedPlaceId && date && allData) {
      if (selectedPlaceId !== lastLoadedPlaceIdRef.current) {
        loadEmployeesForPlaceAndDate(selectedPlaceId, date, allData);
      }
    }
  }, [selectedPlaceId, date, visible, allData]);

  const handleClose = () => {
    setSelectedPlaceId(null);
    setSelectedEmployees([]);
    setDate("");
    setNote("");
    setSelectedPlaceType(null);
    lastLoadedPlaceIdRef.current = null;
    originalEmployeeIdsRef.current = [];
    onClose();
  };

  const toggleEmployeeSelection = (employee) => {
    const isSelected = selectedEmployees.find((e) => e.id === employee.id);
    if (isSelected) {
      setSelectedEmployees(
        selectedEmployees.filter((e) => e.id !== employee.id),
      );
    } else {
      setSelectedEmployees([
        ...selectedEmployees,
        {
          id: employee.id,
          name: employee.name,
          salary: employee.salary?.toString() || "",
          note: "",
          placeDailySalary: selectedPlace?.placeDailySalary?.toString() || "",
        },
      ]);
    }
  };

  const updateEmployeeSalary = (employeeId, newSalary) => {
    setSelectedEmployees(
      selectedEmployees.map((emp) =>
        emp.id === employeeId ? { ...emp, salary: newSalary } : emp,
      ),
    );
  };

  const updateEmployeeNote = (employeeId, newNote) => {
    setSelectedEmployees(
      selectedEmployees.map((emp) =>
        emp.id === employeeId ? { ...emp, note: newNote } : emp,
      ),
    );
  };

  const updateEmployeePlaceDailySalary = (employeeId, newPlaceDailySalary) => {
    setSelectedEmployees(
      selectedEmployees.map((emp) =>
        emp.id === employeeId
          ? { ...emp, placeDailySalary: newPlaceDailySalary }
          : emp,
      ),
    );
  };

  const removeEmployee = (employeeId) => {
    setSelectedEmployees(selectedEmployees.filter((e) => e.id !== employeeId));
  };

  const handleCreateDayWork = async () => {
    if (!date || date.trim() === "") {
      alert("Please enter a date");
      return;
    }
    if (!selectedPlaceId) {
      alert("Please select a place");
      return;
    }

    const invalidSalary = selectedEmployees.find(
      (emp) => !emp.salary || isNaN(emp.salary),
    );
    if (invalidSalary) {
      alert(`Please enter a valid salary for ${invalidSalary.name}`);
      return;
    }

    // Validate PlaceDailySalary if place has daily payment
    if (isPlaceDailyPayment) {
      const invalidPlaceDailySalary = selectedEmployees.find(
        (emp) => !emp.placeDailySalary || isNaN(emp.placeDailySalary),
      );
      if (invalidPlaceDailySalary) {
        alert(
          `Please enter a valid place daily salary for ${invalidPlaceDailySalary.name}`,
        );
        return;
      }
    }

    if (!userData) {
      alert("User data not found");
      return;
    }

    setIsLoading(true);
    try {
      const data = {
        UserId: userData.id,
        PlaceId: selectedPlaceId,
        Date: date,
        Note: note.trim(),
        Employees: selectedEmployees.map((emp) => ({
          EmployeeId: emp.id,
          Salary: parseFloat(emp.salary),
          Note: emp.note || "",
          PlaceDailySalary:
            isPlaceDailyPayment && emp.placeDailySalary
              ? parseFloat(emp.placeDailySalary)
              : null,
        })),
        WhoMake: userData.id,
      };

      const response = await axios.post(
        `${MyDomain}DailyWork/CreateDayWork`,
        data,
      );

      if (response.status === 200) {
        localStorage.setItem("allData", JSON.stringify(response.data));
        alert("Work entry created successfully!");
        handleClose();
        if (onSuccess) onSuccess(response.data);
      }
    } catch (error) {
      console.error("Error creating day work:", error);
      const errorData = error.response?.data;

      // Structured conflict error — matches React Native version
      if (
        (errorData?.errorType === "EMPLOYEE_CONFLICT" ||
          errorData?.ErrorType === "EMPLOYEE_CONFLICT") &&
        (errorData?.conflicts || errorData?.Conflicts)
      ) {
        const conflictDate = errorData.date || errorData.Date || date;
        const conflicts = errorData.conflicts || errorData.Conflicts;

        let conflictMessage = `Employee Conflict on ${conflictDate}:\n\n`;
        conflicts.forEach((conflict) => {
          const empName = conflict.employeeName || conflict.EmployeeName;
          const placeName = conflict.placeName || conflict.PlaceName;
          const addedBy = conflict.addedBy || conflict.AddedBy;
          conflictMessage += `• ${empName} (at ${placeName}, added by ${addedBy})\n`;
        });

        alert(conflictMessage);
      } else {
        const errorMessage =
          errorData?.message ||
          (typeof errorData === "string" ? errorData : null) ||
          "Failed to create work entry";
        alert(`Error: ${errorMessage}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!visible || !allData) return null;

  const isUpdateMode = preselectedEmployees.length > 0;

  // Disable Update button if all original employees were removed
  const allOriginalsRemoved =
    isUpdateMode &&
    originalEmployeeIdsRef.current.length > 0 &&
    originalEmployeeIdsRef.current.every(
      (id) => !selectedEmployees.find((e) => e.id === id),
    );

  const isFormValid =
    date &&
    selectedPlaceId &&
    selectedEmployees.length > 0 &&
    !allOriginalsRemoved;

  const selectedPlace = allData.myPlaces?.find((p) => p.id === selectedPlaceId);
  const isPlaceDailyPayment = selectedPlace?.paidWay === 2;

  const filteredPlaces = selectedPlaceType
    ? (allData.myPlaces || []).filter(
        (place) => place.placeType === selectedPlaceType && place.active === 1,
      )
    : (allData.myPlaces || []).filter((place) => place.active === 1);

  const activeEmployees = (allData.myEmployees || []).filter(
    (emp) => emp.active === 1,
  );

  const getManagerName = (managerId) => {
    const manager = (allData.myEmployees || []).find((e) => e.id === managerId);
    return manager?.name || "Me";
  };

  // Split array into pairs for the 2-row horizontal grid
  const toPairs = (arr) => {
    const pairs = [];
    for (let i = 0; i < arr.length; i += 2) {
      pairs.push([arr[i], arr[i + 1]]);
    }
    return pairs;
  };

  const placePairs = toPairs(filteredPlaces);
  const employeePairs = toPairs(activeEmployees);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end sm:items-center justify-center z-50">
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-4xl max-h-[92vh] flex flex-col animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-gray-100 px-5 pt-3 pb-4">
          <div className="w-9 h-1 bg-gray-300 rounded-full mx-auto mb-4 sm:hidden" />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Day Work</h2>
                <p className="text-sm text-gray-500">
                  {isUpdateMode ? "Update assignments" : "Create new entry"}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div className="space-y-6">
            {/* ── Date Input ── */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Date <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-3">
                <div className="flex-1 flex items-center bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 focus-within:border-blue-400 transition">
                  <Calendar className="w-5 h-5 text-gray-500 mr-3 flex-shrink-0" />
                  <input
                    type="text"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    placeholder="DD-MM-YYYY"
                    maxLength={10}
                    className="flex-1 bg-transparent outline-none text-gray-900"
                  />
                </div>
                <button
                  onClick={() => setDate(getTodayDate())}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-xl transition font-semibold"
                >
                  <Calendar className="w-4 h-4" />
                  <span>Today</span>
                </button>
              </div>
            </div>

            {/* ── Place Selection ── */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Place <span className="text-red-500">*</span>
              </label>

              {/* Selected place display card */}
              {selectedPlace && (
                <div className="flex items-center gap-3 bg-blue-50 border-2 border-blue-500 rounded-xl p-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    {selectedPlace.placeType === 1 ? (
                      <Building className="w-5 h-5 text-blue-600" />
                    ) : (
                      <Hammer className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                      Selected Place
                    </p>
                    <p className="text-sm font-bold text-blue-800 truncate">
                      {selectedPlace.place}
                    </p>
                    {selectedPlace.paidWay && (
                      <span className="inline-flex items-center gap-1 mt-1 bg-gray-100 text-gray-600 text-[10px] font-semibold px-2 py-0.5 rounded-md">
                        <Calendar className="w-3 h-3" />
                        {selectedPlace.paidWay === 1
                          ? "Monthly"
                          : selectedPlace.paidWay === 2
                            ? "Daily"
                            : "Global"}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Place type filter */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[
                  { label: "All", value: null, Icon: List },
                  { label: "Permanent", value: 1, Icon: Building },
                  { label: "Temporary", value: 2, Icon: Hammer },
                ].map(({ label, value, Icon }) => (
                  <button
                    key={label}
                    onClick={() => setSelectedPlaceType(value)}
                    className={`flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg border-2 transition font-semibold text-xs ${
                      selectedPlaceType === value
                        ? "bg-blue-600 border-blue-600 text-white"
                        : "bg-gray-50 border-gray-200 text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {label}
                  </button>
                ))}
              </div>

              {/* Horizontal 2-row place grid */}
              {filteredPlaces.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                  <Building className="w-10 h-10 text-gray-300 mb-2" />
                  <p className="text-sm text-gray-400">No places found</p>
                </div>
              ) : (
                <div className="overflow-x-auto pb-2 hide-scrollbar">
                  <div className="flex gap-3" style={{ width: "max-content" }}>
                    {placePairs.map((pair, colIdx) => (
                      <div key={colIdx} className="flex flex-col gap-3">
                        {pair.map(
                          (place) =>
                            place && (
                              <button
                                key={place.id}
                                onClick={() => {
                                  setSelectedPlaceId(place.id);
                                  // Sync placeDailySalary on all employees when place changes
                                  if (place.paidWay === 2) {
                                    setSelectedEmployees((prev) =>
                                      prev.map((emp) => ({
                                        ...emp,
                                        placeDailySalary:
                                          place.placeDailySalary?.toString() ||
                                          "",
                                      })),
                                    );
                                  }
                                }}
                                className={`relative w-36 rounded-xl border-2 p-3 text-left transition ${
                                  selectedPlaceId === place.id
                                    ? "bg-blue-50 border-blue-500 shadow-sm"
                                    : "bg-gray-50 border-gray-200 hover:border-gray-300"
                                }`}
                              >
                                {selectedPlaceId === place.id && (
                                  <CheckCircle className="absolute top-2 right-2 w-5 h-5 text-blue-500" />
                                )}
                                <p
                                  className={`text-xs font-semibold leading-tight mb-2 pr-5 min-h-[2rem] ${
                                    selectedPlaceId === place.id
                                      ? "text-blue-700"
                                      : "text-gray-700"
                                  }`}
                                >
                                  {place.place}
                                </p>
                                <span
                                  className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded ${
                                    place.placeType === 1
                                      ? "bg-blue-100 text-blue-700"
                                      : "bg-yellow-100 text-yellow-700"
                                  }`}
                                >
                                  {place.placeType === 1
                                    ? "Permanent"
                                    : "Temporary"}
                                </span>
                              </button>
                            ),
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Employee Selection ── */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Employees <span className="text-red-500">*</span>
              </label>

              {activeEmployees.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                  <Users className="w-10 h-10 text-gray-300 mb-2" />
                  <p className="text-sm text-gray-400">No active employees</p>
                </div>
              ) : (
                <div className="overflow-x-auto pb-2 hide-scrollbar">
                  <div className="flex gap-3" style={{ width: "max-content" }}>
                    {employeePairs.map((pair, colIdx) => (
                      <div key={colIdx} className="flex flex-col gap-3">
                        {pair.map(
                          (employee) =>
                            employee && (
                              <button
                                key={employee.id}
                                onClick={() =>
                                  toggleEmployeeSelection(employee)
                                }
                                className={`relative w-32 rounded-xl border-2 p-3 text-left transition ${
                                  selectedEmployees.find(
                                    (e) => e.id === employee.id,
                                  )
                                    ? "bg-blue-50 border-blue-500 shadow-sm"
                                    : "bg-gray-50 border-gray-200 hover:border-gray-300"
                                }`}
                              >
                                {/* check / add icon */}
                                <div className="absolute top-2 right-2">
                                  {selectedEmployees.find(
                                    (e) => e.id === employee.id,
                                  ) ? (
                                    <CheckCircle className="w-5 h-5 text-blue-500" />
                                  ) : (
                                    <PlusCircle className="w-5 h-5 text-gray-300" />
                                  )}
                                </div>

                                <p
                                  className={`text-xs font-semibold leading-tight mb-2 pr-5 min-h-[2rem] ${
                                    selectedEmployees.find(
                                      (e) => e.id === employee.id,
                                    )
                                      ? "text-blue-700"
                                      : "text-gray-700"
                                  }`}
                                >
                                  {employee.name}
                                </p>

                                {/* Salary badge */}
                                <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-md px-1.5 py-0.5 w-fit">
                                  <DollarSign className="w-3 h-3 text-gray-400" />
                                  <span className="text-[10px] font-semibold text-gray-500">
                                    {employee.salary} ₪
                                  </span>
                                </div>
                              </button>
                            ),
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Selected count badge */}
              {selectedEmployees.length > 0 && (
                <div className="flex items-center gap-2 mt-3 bg-blue-50 px-3 py-2 rounded-lg w-fit">
                  <CheckCircle className="w-4 h-4 text-blue-500" />
                  <span className="text-xs font-semibold text-blue-600">
                    {selectedEmployees.length} selected
                  </span>
                </div>
              )}
            </div>

            {/* ── Selected Employees — salary / note cards ── */}
            {selectedEmployees.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
                  <List className="w-4 h-4 text-gray-500" />
                  <h3 className="text-sm font-semibold text-gray-700">
                    Assign Salaries & Notes ({selectedEmployees.length})
                  </h3>
                </div>

                <div className="space-y-3">
                  {selectedEmployees.map((employee, index) => {
                    const existingWork = (allData.myDailyWork || []).find(
                      (work) =>
                        work.employeeId === employee.id &&
                        work.date === date &&
                        work.placeId === selectedPlaceId,
                    );

                    return (
                      <div
                        key={employee.id}
                        className="bg-gray-50 rounded-xl p-4 border border-gray-200 space-y-2"
                      >
                        {/* Employee header */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-sm font-bold">
                                {index + 1}
                              </span>
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900 text-sm">
                                {employee.name}
                              </p>
                              {existingWork?.whoMake && (
                                <div className="flex items-center gap-1 mt-0.5">
                                  <Shield className="w-3 h-3 text-purple-500" />
                                  <span className="text-[10px] font-semibold text-purple-600">
                                    {getManagerName(existingWork.whoMake)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => removeEmployee(employee.id)}
                            className="p-1.5 hover:bg-red-50 rounded-lg transition"
                          >
                            <X className="w-5 h-5 text-red-400" />
                          </button>
                        </div>

                        {/* Salary input */}
                        <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2.5 border border-gray-200">
                          <DollarSign className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <input
                            type="number"
                            value={employee.salary}
                            onChange={(e) =>
                              updateEmployeeSalary(employee.id, e.target.value)
                            }
                            placeholder="Enter salary"
                            className="flex-1 outline-none text-gray-900 text-sm"
                          />
                          <span className="text-gray-500 font-semibold text-sm">
                            ₪
                          </span>
                        </div>

                        {/* Place daily salary — only when paidWay === 2 */}
                        {isPlaceDailyPayment && (
                          <div className="flex items-center gap-2 bg-purple-50 rounded-lg px-3 py-2.5 border border-purple-200">
                            <Wallet className="w-4 h-4 text-purple-500 flex-shrink-0" />
                            <input
                              type="number"
                              value={employee.placeDailySalary}
                              onChange={(e) =>
                                updateEmployeePlaceDailySalary(
                                  employee.id,
                                  e.target.value,
                                )
                              }
                              placeholder="Place daily salary"
                              className="flex-1 outline-none text-gray-900 text-sm bg-transparent"
                            />
                            <span className="text-purple-500 font-semibold text-sm">
                              ₪
                            </span>
                          </div>
                        )}

                        {/* Note input */}
                        <div className="flex items-start gap-2 bg-white rounded-lg px-3 py-2.5 border border-gray-200">
                          <FileText className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <textarea
                            value={employee.note}
                            onChange={(e) =>
                              updateEmployeeNote(employee.id, e.target.value)
                            }
                            placeholder="Add note for employee"
                            rows={2}
                            maxLength={150}
                            className="flex-1 outline-none text-gray-900 text-sm resize-none"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Manager info */}
                <div className="flex items-center gap-3 bg-purple-50 rounded-xl p-4 border border-purple-100">
                  <div className="w-11 h-11 bg-purple-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">
                      Recording Manager
                    </p>
                    <p className="text-sm font-bold text-gray-900">
                      {userData?.name || "You"}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 bg-green-100 px-3 py-1 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-xs font-semibold text-green-700">
                      Active
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Warning: all originals removed */}
            {/* {allOriginalsRemoved && (
              <div className="flex items-start gap-3 bg-red-50 rounded-xl p-4 border border-red-200">
                <Info className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">
                  You've removed all employees from this work entry. Please keep
                  at least one to save the update.
                </p>
              </div>
            )} */}

            {/* General note */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                General Note
              </label>
              <div className="flex items-start gap-3 bg-gray-50 border-2 border-gray-200 rounded-xl p-4 focus-within:border-blue-400 transition">
                <FileText className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add general note (optional)"
                  rows={3}
                  maxLength={200}
                  className="flex-1 bg-transparent outline-none text-gray-900 resize-none"
                />
              </div>
            </div>

            {/* Info card */}
            {selectedEmployees.length > 0 && !allOriginalsRemoved && (
              <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-4 border border-blue-200">
                <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-900">
                  {isUpdateMode
                    ? "Changes will update existing work entries for the selected employees."
                    : `Creating ${selectedEmployees.length} work ${
                        selectedEmployees.length === 1 ? "entry" : "entries"
                      } for ${selectedPlace?.place || "selected place"}.`}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-5 py-4">
          <button
            onClick={handleCreateDayWork}
            disabled={isLoading}
            className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold transition ${
              !isLoading
                ? "bg-blue-600 hover:bg-blue-700 text-white shadow-md"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>
                  {isUpdateMode ? "Update Work Entry" : "Create Work Entry"}
                </span>
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin   { animation: spin 1s linear infinite; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type="number"] { -moz-appearance: textfield; }
      `}</style>
    </div>
  );
}
